import moment from 'moment'
import type { IntlShape } from 'react-intl'
import { SHORT_DAY_NAME_DAY_MONTH, DAY_MONTH_NAME_SHORT, DAY_ONLY } from 'constants/dateFormats'

const DAY_LONG_MONTH = 'D MMMM'

export function formatDayLabel(dateStr: string): string {
  return moment(dateStr).format(SHORT_DAY_NAME_DAY_MONTH)
}

function formatDateRange(start: moment.Moment, end: moment.Moment): string {
  if (start.isSame(end, 'day')) {
    return start.format(DAY_LONG_MONTH)
  }

  if (start.isSame(end, 'month')) {
    return `${start.format(DAY_ONLY)}-${end.format(DAY_LONG_MONTH)}`
  }

  return `${start.format(DAY_MONTH_NAME_SHORT)} - ${end.format(DAY_MONTH_NAME_SHORT)}`
}

export function formatStopDateRange(startDateStr: string, endDateStr: string): string {
  return formatDateRange(moment(startDateStr), moment(endDateStr))
}

export function computeStayDateRange(dayDate: string, dayOfStay: number, totalDays: number): string {
  const stayStart = moment(dayDate).subtract(dayOfStay - 1, 'days')
  const stayEnd = stayStart.clone().add(totalDays - 1, 'days')
  return formatDateRange(stayStart, stayEnd)
}

/**
 * Returns a date-aware day label without i18n. Use for LLM messages,
 * data attributes, and other non-translatable contexts.
 */
export function getDayLabel(dayNumber: number, dateStr?: string): string {
  if (dateStr) return formatDayLabel(dateStr)
  return `Day ${dayNumber}`
}

/**
 * Returns a date-aware day label with i18n support. Use for all
 * user-facing UI text rendered inside React components.
 */
export function getLocalisedDayLabel(intl: IntlShape, dayNumber: number, dateStr?: string): string {
  if (dateStr) return formatDayLabel(dateStr)
  return intl.formatMessage(
    {
      id: 'nKq1zQQctl_T9caEJO6m',
      defaultMessage: 'Day {dayNumber}',
      description: 'Label for a specific day number in the trip planner itinerary',
    },
    { dayNumber },
  )
}

export function computeTripDatesFromStops(
  stops: ReadonlyArray<{ startDate?: string; endDate?: string }> | undefined,
): { startDate: string; endDate: string } | undefined {
  if (!stops) return undefined

  let minStart: string | undefined
  let maxEnd: string | undefined

  for (const stop of stops) {
    if (!stop.startDate || !stop.endDate) continue
    if (!minStart || stop.startDate < minStart) minStart = stop.startDate
    if (!maxEnd || stop.endDate > maxEnd) maxEnd = stop.endDate
  }

  if (!minStart || !maxEnd) return undefined
  return { startDate: minStart, endDate: maxEnd }
}
